import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Decorative } from "@faire/design-tokens";
import { ContentContainer, VanillaBackground } from "@faire/retailer-visitor-shared/components/GiftGuide/common";
import { WhiteBackground } from "@faire/retailer-visitor-shared/views/featured/wholesale-gifts/components/common";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { optimizeImageUrl } from "@faire/web--source/common/transform-images";
import { Button } from "@faire/web--source/slate/Button";
import { Column, Row } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { useResponsiveValue } from "@faire/web--source/ui/hooks/useResponsiveValue";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import categoryRoute from "@faire/web-api--source/routes/category/c1CategoryUrlId";
import React from "react";
import styled from "styled-components";
export var DiscoverGifts = function() {
    var viewport = useViewport();
    return /*#__PURE__*/ _jsx(WhiteBackground, {
        children: /*#__PURE__*/ _jsxs(StyledContentContainer, {
            children: [
                /*#__PURE__*/ _jsx(Typography, {
                    as: "h2",
                    variant: viewport.isTabletAndAbove ? "sectionHeaderSansMedium" : "subheadingSansMedium",
                    align: "center",
                    children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                        id: "amHWB/",
                        defaultMessage: [
                            {
                                type: 0,
                                value: "Discover new wholesale gift brands for your boutique"
                            }
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(Spacer, {
                    height: "7x"
                }),
                /*#__PURE__*/ _jsxs(Tiles, {
                    justify: "center",
                    children: [
                        /*#__PURE__*/ _jsxs(Column, {
                            align: "flex-start",
                            children: [
                                /*#__PURE__*/ _jsx(DiscoverGiftsImageOne, {}),
                                /*#__PURE__*/ _jsx(Spacer, {
                                    height: "3x"
                                }),
                                /*#__PURE__*/ _jsx(Typography, {
                                    variant: viewport.isDesktopAndAbove ? "displayMSerifRegular" : "displaySSerifRegular",
                                    children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                                        id: "5nYiOH",
                                        defaultMessage: [
                                            {
                                                type: 0,
                                                value: "For the Foodie"
                                            }
                                        ]
                                    })
                                }),
                                /*#__PURE__*/ _jsx(Spacer, {
                                    height: 4
                                }),
                                /*#__PURE__*/ _jsx(Typography, {
                                    variant: "subheadingSansRegular",
                                    children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                                        id: "JYb7AA",
                                        defaultMessage: [
                                            {
                                                type: 0,
                                                value: "Stock your store with unique gifts they\u2019ll love."
                                            }
                                        ]
                                    })
                                }),
                                /*#__PURE__*/ _jsx(Spacer, {
                                    height: "3x"
                                }),
                                /*#__PURE__*/ _jsx(StyledButton, {
                                    variant: "primary",
                                    linkTo: categoryRoute("For the Foodie"),
                                    children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                                        id: "UAeu4k",
                                        defaultMessage: [
                                            {
                                                type: 0,
                                                value: "Shop Now"
                                            }
                                        ]
                                    })
                                })
                            ]
                        }),
                        viewport.isTabletAndAbove ? /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx(Spacer, {
                                    width: viewport.isDesktopAndAbove ? "4x" : "2x"
                                }),
                                /*#__PURE__*/ _jsx(StyledVanillaBackground, {
                                    children: /*#__PURE__*/ _jsx(DiscoverGiftsImageTwo, {})
                                })
                            ]
                        }) : null
                    ]
                })
            ]
        })
    });
};
var DiscoverGiftsImageOne = function() {
    var url = "https://cdn.faire.com/fastly/b28b734e30d1029d6a2262a38b5ba1040ea4973abcaf71d1e37552b4f1744af1.png";
    var width = useResponsiveValue({
        mobileAndAbove: 327,
        tabletAndAbove: 320,
        desktopAndAbove: 473
    });
    var height = useResponsiveValue({
        mobileAndAbove: 338,
        tabletAndAbove: 315,
        desktopAndAbove: 465
    });
    var isSSR = useIsSSR();
    return /*#__PURE__*/ _jsx(Image, {
        alt: "adding olive oil to spaghetti",
        src: optimizeImageUrl({
            url: url
        }, isSSR, {
            width: width,
            height: height,
            resizeMethod: "fit"
        })
    });
};
var DiscoverGiftsImageTwo = function() {
    var url = "https://cdn.faire.com/fastly/5e33ca6e32c8d845d19e2da51d57e9e6f57baa64e6fca915ed8e42d56686f481.png";
    var width = useResponsiveValue({
        mobileAndAbove: 280,
        desktopAndAbove: 414
    });
    var height = useResponsiveValue({
        mobileAndAbove: 375,
        desktopAndAbove: 482
    });
    var isSSR = useIsSSR();
    return /*#__PURE__*/ _jsx(Image, {
        alt: "olive oil containers",
        src: optimizeImageUrl({
            url: url
        }, isSSR, {
            width: width,
            height: height,
            resizeMethod: "fit"
        })
    });
};
var Image = styled.img.withConfig({
    componentId: "sc-3d6b037c-0"
})([
    "display:block;width:100%;"
]);
var StyledButton = styled(Button).withConfig({
    componentId: "sc-3d6b037c-1"
})([
    "width:100%;",
    "{width:fit-content;min-width:200px;}"
], responsiveMediaQueries.tabletAndAbove);
var StyledContentContainer = styled(ContentContainer).withConfig({
    componentId: "sc-3d6b037c-2"
})([
    "padding:",
    ";"
], getSpacing(0, "3x"));
var Tiles = styled(Row).withConfig({
    componentId: "sc-3d6b037c-3"
})([
    "",
    "{margin:",
    ";}",
    "{margin:0 104px;}"
], responsiveMediaQueries.tabletAndAbove, getSpacing(0, "3x"), responsiveMediaQueries.desktopAndAbove);
var StyledVanillaBackground = styled(VanillaBackground).withConfig({
    componentId: "sc-3d6b037c-4"
})([
    "background:",
    ";justify-content:center;padding:",
    ";",
    "{padding:",
    ";}"
], Decorative.tint.neutral, getSpacing(0, "6x"), responsiveMediaQueries.desktopAndAbove, getSpacing(0, "9x"));
